import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store';

import { ScheduleTemplate } from './scheduleTemplates.types';

const STORAGE_KEY = 'lifemd-schedule-templates';

interface ScheduleTemplatesState {
  templates: ScheduleTemplate[];
}

/**
 * Loads templates from localStorage if available.
 *
 * Why:
 *   Templates are stored client-side until a backend API is built.
 *   localStorage persists across page refreshes and browser sessions.
 */
const loadFromStorage = (): ScheduleTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ScheduleTemplate[];
      // Migration: skip templates with old `shifts` format (pre-week1/week2)
      return parsed.filter((t) => Array.isArray(t.week1));
    }
  } catch {
    // Silently fall back to empty if localStorage is unavailable or corrupt
  }
  return [];
};

/**
 * Saves templates to localStorage.
 */
const saveToStorage = (templates: ScheduleTemplate[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch {
    // Silently fail if localStorage is full or unavailable
  }
};

const initialState: ScheduleTemplatesState = {
  templates: loadFromStorage()
};

const scheduleTemplatesSlice = createSlice({
  initialState,
  name: 'scheduleTemplates',
  reducers: {
    addTemplate: (state, action: PayloadAction<ScheduleTemplate>) => {
      state.templates.push(action.payload);
      saveToStorage(state.templates);
    },
    removeTemplate: (state, action: PayloadAction<string>) => {
      state.templates = state.templates.filter((t) => t._id !== action.payload);
      saveToStorage(state.templates);
    },
    updateTemplate: (state, action: PayloadAction<ScheduleTemplate>) => {
      const index = state.templates.findIndex((t) => t._id === action.payload._id);
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
      saveToStorage(state.templates);
    }
  }
});

export const { addTemplate, removeTemplate, updateTemplate } = scheduleTemplatesSlice.actions;

export const selectTemplates = (state: RootState) => state.scheduleTemplates.templates;

export default scheduleTemplatesSlice.reducer;
